import { Either, Option, ReadonlyArray } from "@morphism/fp";
import * as Ant from "antd";
import React from "react";
import styled from "styled-components";

import { Asset } from "~/Asset";
import * as Color from "~/Color";
import { DVIR, DVIRs } from "~/DVIR";
import * as Icon from "~/Icon";
import { ID } from "~/ID";
import * as Layout from "~/Layout";
import { Mutable } from "~/Mutable";
import { NotApplicable } from "~/NotApplicable";
import { Order } from "~/Order";
import { Select as GenericSelect } from "~/Select";
import * as Shadow from "~/Shadow";
import * as Typography from "~/Typography";

const foldDefect = (asset: Asset.OptionalResult): Option<Asset> =>
  pipe(
    asset,
    Either.fold(() => Option.none, Option.fromNullable)
  );

export function Defects({
  type,
  dvir,
}: {
  readonly type: Defects.Type;
  readonly dvir: DVIR;
}) {
  return pipe(
    type === "vehicle" ? [dvir.vehicle as Asset.Result] : dvir.trailers,
    ReadonlyArray.rights,
    ReadonlyArray.filter((asset) => !!asset),
    ReadonlyArray.sort(Asset.order()),
    ReadonlyArray.mapWithIndex((index, asset) =>
      pipe(Defects.forAsset(dvir)(asset), ([first, second, ...rest]) => (
        <Defects.Row key={index}>
          {second ? (
            <Ant.Dropdown
              overlay={pipe(
                [first, second, ...rest],
                ReadonlyArray.map(({ defect }) => (
                  <div key={defect.id}>{defect.type.name}</div>
                )),
                (defects) => (
                  <Defects.Dropdown>{defects}</Defects.Dropdown>
                )
              )}
            >
              <Defects.Pill>
                <Layout.Inline.Component right>
                  {[first, second, ...rest].length} reported defects
                </Layout.Inline.Component>
                <Icon.ChevronDown />
              </Defects.Pill>
            </Ant.Dropdown>
          ) : first ? (
            <Defects.Pill>{first.defect.type.name}</Defects.Pill>
          ) : (
            <NotApplicable>None</NotApplicable>
          )}
        </Defects.Row>
      ))
    ),
    ([first, ...rest]) =>
      first ? <>{[first, ...rest]}</> : <NotApplicable>None</NotApplicable>
  );
}

export namespace Defects {
  export type Type = "vehicle" | "trailers";

  export const column = (type: "vehicle" | "trailers") => ({
    dvirs,
    types,
    onChange,
  }: {
    // eslint-disable-next-line functional/prefer-readonly-type
    readonly dvirs: DVIR[];
    readonly types?: readonly ID[];
    readonly onChange: (defects?: readonly ID[]) => void;
  }) => ({
    title: "Defects",
    dataIndex: `${type}-defects`,

    filteredValue: types && [`${type}-defects`],
    filterDropdown: (props: { readonly confirm: () => void }) => (
      <Filter {...{ type, dvirs, types, onChange }} {...props} />
    ),
    onFilter: (_: unknown, dvir: DVIR) =>
      pipe(dvir, getFilterFunc(type, types)),
    render: (_: unknown, dvir: DVIR) => <Defects type={type} dvir={dvir} />,
    sorter: (a: DVIR, b: DVIR): Order.Ordering =>
      pipe([a, b], ReadonlyArray.map(forType(type)), ([a, b]) =>
        pipe(Order.number().compare(a.length, b.length), (lengths) =>
          lengths !== Order.equal()
            ? lengths
            : pipe(
                [a, b],
                ReadonlyArray.map(
                  flow(
                    ReadonlyArray.map(({ defect: { type } }) => type.name),
                    ReadonlyArray.sort(Order.string())
                  )
                ),
                ([[a], [b]]) => Order.optionally(Order.string().compare)(a, b)
              )
        )
      ),
  });

  export const getFilterFunc = (type: Type, types: readonly ID[] = []) => (
    dvir: DVIR
  ) => {
    const ids = types.map(({ id }) => id);
    return !ids[0]
      ? true
      : pipe(
          dvir,
          forType(type),
          ReadonlyArray.findFirst(({ defect }) => ids.includes(defect.type.id)),
          Option.isSome
        );
  };

  export const getFilterFuncForSettings = (
    type: Type,
    settings: DVIRs.Table.Settings
  ) =>
    getFilterFunc(
      type,
      type === "vehicle"
        ? settings.defectTypes?.vehicles
        : settings.defectTypes?.trailers
    );

  export const forAsset = (dvir: DVIR) => ({ asset }: Asset) =>
    pipe(
      dvir.defects,
      ReadonlyArray.filter(({ defect }) =>
        pipe(
          defect.asset,
          Either.fold(
            () => false,
            (defect) => (defect?.asset ? defect?.asset.id === asset.id : false)
          )
        )
      )
    );

  export const forType = (type: Type) => (dvir: DVIR) => {
    return pipe(
      dvir.defects,
      ReadonlyArray.filter(({ defect: { asset } }) =>
        pipe(
          asset,
          foldDefect,
          Option.fold(
            () => false,
            () => true
          ),
          (hasAsset) => type === "vehicle" && hasAsset
        )
      )
    );
  };

  export const Dropdown = styled.div`
    background: white;
    padding: ${Layout.Inline.padding()};
    ${Shadow.Box.styles(Shadow.medium())}
    font-size: ${Typography.FontSize.data()};
  `;

  export const Row = styled.div`
    margin-bottom: ${Layout.Block.padding()};
    &:last-of-type {
      margin-bottom: 0;
    }
  `;

  export const Pill = styled.span`
    padding: 0.2em 0.5em;
    background: ${Color.translucentColor(Color.gray(), 0.3)};
    border: 1px solid ${Color.gray()};
    border-radius: 0.5em;
  `;

  export const Filter = ({
    type,
    dvirs,
    loading,
    types,
    onChange,
    confirm,
  }: DVIRs.Table.Filter.Props &
    Parameters<ReturnType<typeof column>>[0] & { readonly type: Type }) => {
    type Option = { readonly key: string; readonly value: string };

    const selected = (types ?? []).map(({ id }) => id);
    const options = pipe(
      dvirs,
      ReadonlyArray.chain(
        flow(
          forType(type),
          ReadonlyArray.map(({ defect: { type: { id, name } } }) => ({
            key: id,
            value: name,
          }))
        )
      ),
      ReadonlyArray.sort<Option>(
        Order.fromCompare(({ value: a }, { value: b }) =>
          Order.optionally(Order.string().compare)(a, b)
        )
      ),
      ReadonlyArray.uniq({ equals: ({ key: a }, { key: b }) => a === b })
    );

    const value = pipe(
      options,
      ReadonlyArray.filterMap(({ key, value }) =>
        selected.includes(key) ? Option.some(value) : Option.none
      )
    );

    return (
      <DVIRs.Table.Filter confirm={confirm} onReset={types?.[0] && onChange}>
        <GenericSelect
          loading={loading}
          style={{ width: 200 }}
          dropdownMatchSelectWidth
          placeholder="Filter by defect type"
          mode="multiple"
          bordered={false}
          options={options as Mutable<typeof options>}
          value={value as Mutable<typeof value>}
          onChange={
            ((_value: never, selected: readonly { readonly key: string }[]) =>
              onChange(selected.map(({ key: id }) => ({ id })))) as never
          }
        />
      </DVIRs.Table.Filter>
    );
  };
}
